// src/components/ChatRoom.jsx
import { useState, useRef } from "react";
import { Send, Paperclip, X, FileText, Image, Video, Play } from "lucide-react";
import { useFirestoreChat } from "../hooks/useFirestoreChat";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";

const CLOUDINARY_CLOUD = "dq81k0qo3";
const CLOUDINARY_PRESET = "nodom_receipts";

export default function ChatRoom({ chatId, title, adhdProfile }) {
  const { messages, loading, sendMessage, bottomRef } = useFirestoreChat(chatId);
  const { user, profile } = useAuth();
  const { t, lang } = useLanguage();
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [attachment, setAttachment] = useState(null); // { file, previewUrl, type }
  const [uploading, setUploading] = useState(false);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  const isDoctorOrAdmin = profile?.role === "doctor" || profile?.role === "admin";
  const isAdmin = profile?.role === "admin";

  // What file types are allowed
  const acceptTypes = isDoctorOrAdmin
    ? "image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx"
    : "image/*,.pdf,.doc,.docx";

  function handleFileSelect(e) {
    const file = e.target.files[0];
    if (!file) return;
    const type = file.type.startsWith("image/") ? "image"
               : file.type.startsWith("video/") ? "video"
               : "file";
    const previewUrl = (type === "image" || type === "video")
      ? URL.createObjectURL(file)
      : null;
    setAttachment({ file, previewUrl, type });
    // reset input so same file can be re-selected
    e.target.value = "";
  }

  function clearAttachment() {
    if (attachment?.previewUrl) URL.revokeObjectURL(attachment.previewUrl);
    setAttachment(null);
  }

async function uploadToCloudinary(file, type) {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("upload_preset", CLOUDINARY_PRESET);
  formData.append("folder", "nodom_chat");

  const isPDF = file.type === "application/pdf";
  const resourceType = type === "video" ? "video" : "image";

  const res = await fetch(
    `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD}/${resourceType}/upload`,
    { method: "POST", body: formData }
  );
  if (!res.ok) throw new Error("Upload failed");
  const data = await res.json();

  // PDF — أضف امتداد .pdf للـ URL حتى يفتح بشكل صحيح
  if (isPDF) {
    const url = data.secure_url;
    return url.endsWith(".pdf") ? url : url.replace(/\.[^/.]+$/, "") + ".pdf";
  }

  return data.secure_url;
}

  async function handleSend() {
    if ((!text.trim() && !attachment) || sending) return;
    setSending(true);

    try {
      if (attachment) {
        setUploading(true);
        const url = await uploadToCloudinary(attachment.file, attachment.type);
        setUploading(false);
        await sendMessage(text.trim(), {
          url,
          mediaType: attachment.type,
          fileName: attachment.file.name
        });
        clearAttachment();
      } else {
        await sendMessage(text.trim());
      }
      setText("");
      textareaRef.current?.focus();
    } catch (err) {
      setUploading(false);
      alert(lang === "ar" ? "فشل الإرسال، حاول مجدداً" : "Send failed, try again");
    }

    setSending(false);
  }

  function handleKey(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  function formatTime(ts) {
    if (!ts) return "";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleTimeString("ar-SA", { hour: "2-digit", minute: "2-digit" });
  }

  return (
    <div>
      {title && <div style={{ marginBottom: "1rem" }}><h3>{title}</h3></div>}
      {adhdProfile && (
        <div className="alert alert-info" style={{ marginBottom: "1rem" }}>
          <strong>ملف الطفل:</strong> {adhdProfile.childName} — عمر: {adhdProfile.childAge}
        </div>
      )}

      <div className="chat-container">
        {/* Messages */}
        <div className="chat-messages">
          {loading && <div className="loading-center"><div className="spinner" /></div>}
          {!loading && messages.length === 0 && (
            <div style={{ textAlign: "center", color: "var(--color-gray-300)", marginTop: "2rem", fontSize: "0.875rem" }}>
              {t("noMessages")}
            </div>
          )}

          {messages.map(msg => {
            const isSent = msg.senderId === user?.uid;
            return (
              <div key={msg.id} style={{ display: "flex", flexDirection: "column", alignItems: isSent ? "flex-end" : "flex-start" }}>
                {!isSent && (
                  <span style={{ fontSize: "0.72rem", color: "var(--color-gray-400)", marginBottom: "0.2rem", padding: "0 0.5rem" }}>
                    {msg.senderName}
                    {msg.senderRole === "doctor" && (
                      <span style={{ color: "var(--color-forest)", marginRight: "0.3rem" }}> • {lang === "ar" ? "طبيب" : "Doctor"}</span>
                    )}
                    {msg.senderRole === "admin" && (
                      <span style={{ color: "#6366f1", marginRight: "0.3rem" }}> • {lang === "ar" ? "مدير" : "Admin"}</span>
                    )}
                  </span>
                )}
                <div className={`chat-bubble ${isSent ? "sent" : "received"}`}>
                  {/* Media content */}
                  {msg.mediaType === "image" && (
                    <a href={msg.mediaUrl} target="_blank" rel="noreferrer">
                      <img
                        src={msg.mediaUrl}
                        alt="صورة"
                        style={{ maxWidth: "100%", maxHeight: 220, borderRadius: 8, display: "block", marginBottom: msg.text ? "0.5rem" : 0, cursor: "pointer" }}
                      />
                    </a>
                  )}
                  {msg.mediaType === "video" && (
                    <video
                      src={msg.mediaUrl}
                      controls
                      style={{ maxWidth: "100%", maxHeight: 200, borderRadius: 8, display: "block", marginBottom: msg.text ? "0.5rem" : 0 }}
                    />
                  )}
                  {msg.mediaType === "file" && (
                    <a
                        href={msg.mediaUrl}
                        target="_blank"
                        rel="noreferrer"
                      style={{
                        display: "flex", alignItems: "center", gap: "0.5rem",
                        padding: "0.5rem 0.75rem",
                        background: isSent ? "rgba(255,255,255,0.15)" : "var(--color-gray-100)",
                        borderRadius: 8, marginBottom: msg.text ? "0.5rem" : 0,
                        color: isSent ? "#fff" : "var(--color-forest)",
                        fontSize: "0.85rem", textDecoration: "none"
                      }}
                    >
                      <FileText size={18} />
                      <span style={{ wordBreak: "break-all" }}>{msg.fileName || "ملف"}</span>
                    </a>
                  )}

                  {/* Text */}
                  {msg.text && <span>{msg.text}</span>}
                  <div className="chat-time">{formatTime(msg.createdAt)}</div>
                </div>
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>

        {/* Attachment preview */}
        {attachment && (
          <div style={{
            padding: "0.6rem 1rem",
            borderTop: "1px solid var(--color-gray-200)",
            background: "var(--color-gray-50)",
            display: "flex", alignItems: "center", gap: "0.75rem"
          }}>
            {attachment.type === "image" && (
              <img src={attachment.previewUrl} alt="" style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 6 }} />
            )}
            {attachment.type === "video" && (
              <div style={{ width: 48, height: 48, background: "var(--color-gray-200)", borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Play size={20} style={{ color: "var(--color-forest)" }} />
              </div>
            )}
            {attachment.type === "file" && (
              <div style={{ width: 48, height: 48, background: "var(--color-gray-200)", borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <FileText size={20} style={{ color: "var(--color-forest)" }} />
              </div>
            )}
            <span style={{ fontSize: "0.82rem", color: "var(--color-gray-600)", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {attachment.file.name}
            </span>
            {uploading && <div className="spinner" style={{ width: 18, height: 18, flexShrink: 0 }} />}
            {!uploading && (
              <button onClick={clearAttachment} className="btn btn-ghost btn-sm" style={{ padding: "0.25rem", flexShrink: 0 }}>
                <X size={16} />
              </button>
            )}
          </div>
        )}

        {/* Input Bar */}
        <div className="chat-input-bar">
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptTypes}
            onChange={handleFileSelect}
            style={{ display: "none" }}
          />

          {/* Attach button */}
          <button
            className="btn btn-ghost"
            style={{ padding: "0.5rem", flexShrink: 0, color: attachment ? "var(--color-forest)" : "var(--color-gray-400)" }}
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || sending}
            title={lang === "ar" ? "إرفاق ملف" : "Attach file"}
          >
            <Paperclip size={20} />
          </button>

          <textarea
            ref={textareaRef}
            className="chat-input"
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={handleKey}
            placeholder={t("typeMessage")}
            rows={1}
            dir="auto"
            disabled={uploading}
          />

          <button
            className="btn btn-primary"
            onClick={handleSend}
            disabled={(!text.trim() && !attachment) || sending || uploading}
            style={{ padding: "0.6rem 1rem", flexShrink: 0 }}
          >
            {uploading ? <div className="spinner" style={{ width: 16, height: 16 }} /> : <Send size={16} />}
          </button>
        </div>
      </div>
    </div>
  );
}